import { useCallback, useState } from "react";
import { api } from "../api/apiClient";
import {
  AiDocumentType,
  AiFieldInput,
  AiFieldSuggestion,
  AiTextAction,
  ImproveTextResponse,
} from "../components/ai/types";

interface PendingRequest {
  documentType: AiDocumentType;
  action: AiTextAction;
  fields: AiFieldInput[];
  focus?: string;
  contextFields?: AiFieldInput[];
}

const failureMessage =
  "The AI suggestion could not be generated. Your original text has not been changed.";

export default function useAiTextImprovement() {
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<AiFieldSuggestion[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);

  const improve = useCallback(async (
    documentType: AiDocumentType,
    action: AiTextAction,
    fields: AiFieldInput[],
    focus?: string,
    contextFields?: AiFieldInput[],
  ) => {
    if (loading) return;
    const request = { documentType, action, fields, focus, contextFields };
    setPendingRequest(request);
    setLoading(true);
    setError("");

    try {
      const result = await api<ImproveTextResponse>("/api/ai/improve-text", {
        method: "POST",
        body: {
          documentType,
          action,
          focus: focus?.trim() || null,
          targetFields: fields.map((field) => field.name),
          fields: Object.fromEntries((contextFields ?? fields)
            .map((field) => [field.name, field.value ?? null])),
        },
        suppressLoading: true,
        notifyError: false,
      });

      setSuggestions(
        fields
          .filter((field) => Boolean((result.fields[field.name] ?? field.value)?.trim()))
          .map((field) => ({
            fieldName: field.name,
            originalValue: field.value?.trim() ?? "",
            suggestedValue: result.fields[field.name] ?? field.value?.trim() ?? "",
          })),
      );
      setWarnings(result.warnings ?? []);
    } catch {
      setSuggestions([]);
      setWarnings([]);
      setError(failureMessage);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const retry = useCallback(() => {
    if (pendingRequest) {
      void improve(
        pendingRequest.documentType,
        pendingRequest.action,
        pendingRequest.fields,
        pendingRequest.focus,
        pendingRequest.contextFields,
      );
    }
  }, [improve, pendingRequest]);

  const updateSuggestion = (fieldName: string, value: string) => {
    setSuggestions((current) => current.map((item) =>
      item.fieldName === fieldName ? { ...item, suggestedValue: value, decision: undefined } : item,
    ));
  };

  const setDecision = (fieldName: string, decision: "suggestion" | "original") => {
    setSuggestions((current) => current.map((item) =>
      item.fieldName === fieldName ? { ...item, decision } : item,
    ));
  };

  const cancel = () => {
    setSuggestions([]);
    setWarnings([]);
    setError("");
    setPendingRequest(null);
  };

  return {
    loading,
    suggestions,
    warnings,
    error,
    improve,
    retry,
    updateSuggestion,
    setDecision,
    cancel,
  };
}
